import { Goal } from '@/api/generated/model';
import { goalsService } from '@/api/services/goalsService';
import { GoalCard } from '@/components/goals/GoalCard';
import { GoalDetails } from '@/components/goals/GoalDetails';
import { GoalDialog } from '@/components/goals/GoalDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Loading } from '@/components/ui/loading';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/i18n/LanguageContext';
import { getGoalStatus } from '@/lib/utils.ts';
import { Plus, Search, Target } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const Goals = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { goalId } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('All Priorities');
  const [selectedStatus, setSelectedStatus] = useState('All Statuses');
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const loadGoals = async () => {
    try {
      const goalsData = await goalsService.getGoals();
      setGoals(goalsData);

      // If there's a goalId in the URL, find and select that goal
      if (goalId) {
        const goal = goalsData.find((g) => g.id === goalId);
        if (goal) {
          setSelectedGoal(goal);
          setIsDetailsDialogOpen(true);
        }
      }
    } catch (error) {
      console.error('Failed to load goals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGoals();
  }, [goalId]);

  const handleAddGoal = async (data: Partial<Goal>) => {
    try {
      await goalsService.createGoal(data);
      setIsGoalDialogOpen(false);
      loadGoals();
    } catch (error) {
      console.error('Failed to create goal:', error);
    }
  };

  const handleEditGoal = async (data: Partial<Goal>) => {
    if (!selectedGoal) return;
    try {
      await goalsService.updateGoal(selectedGoal.id, data);
      setIsGoalDialogOpen(false);
      setSelectedGoal(null);
      loadGoals();
    } catch (error) {
      console.error('Failed to update goal:', error);
    }
  };

  const handleDeleteGoal = async () => {
    if (!selectedGoal) return;
    try {
      await goalsService.deleteGoal(selectedGoal.id);
      setIsDeleteDialogOpen(false);
      setSelectedGoal(null);
      navigate('/goals'); // Navigate back to goals list after deletion
      loadGoals();
    } catch (error) {
      console.error('Failed to delete goal:', error);
    }
  };

  const openAddDialog = () => {
    setDialogMode('add');
    setSelectedGoal(null);
    setIsGoalDialogOpen(true);
  };

  const openEditDialog = (goal: Goal) => {
    setDialogMode('edit');
    setSelectedGoal(goal);
    setIsGoalDialogOpen(true);
  };

  const openGoalDetails = (goal: Goal) => {
    setSelectedGoal(goal);
    setIsDetailsDialogOpen(true);
    navigate(`/goals/${goal.id}`);
  };

  const closeGoalDetails = () => {
    setIsDetailsDialogOpen(false);
    navigate('/goals');
  };

  const filteredGoals = goals.filter((goal) => {
    // Filter by search term
    const matchesSearch = goal.name.toLowerCase().includes(searchTerm.toLowerCase());

    // Filter by priority
    const matchesPriority =
      selectedPriority === 'All Priorities' || goal.priority === selectedPriority;

    // Filter by status
    const status = getGoalStatus(goal);
    const matchesStatus = selectedStatus === 'All Statuses' || status === selectedStatus;

    return matchesSearch && matchesPriority && matchesStatus;
  });

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">{t('goals.title')}</h1>
        <Button onClick={openAddDialog}>
          <Plus className="mr-2 h-4 w-4" /> {t('goals.actions.add')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('goals.title')}</CardTitle>
          <CardDescription>{t('goals.description')}</CardDescription>
        </CardHeader>

        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-80">
                <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
                <Input
                  type="search"
                  placeholder={t('goals.search.placeholder')}
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="flex flex-1 flex-col gap-2 sm:flex-row">
                <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                  <SelectTrigger className="w-full sm:w-auto">
                    <SelectValue placeholder={t('goals.filters.priority.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    {['All Priorities', 'mandatory', 'nice-to-have'].map((priority) => (
                      <SelectItem key={priority} value={priority}>
                        {priority === 'All Priorities'
                          ? t('goals.filters.priority.all')
                          : t(`goals.priorities.${priority}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-full sm:w-auto">
                    <SelectValue placeholder={t('goals.filters.status.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    {['All Statuses', 'onTrack', 'atRisk', 'behind'].map((status) => (
                      <SelectItem key={status} value={status}>
                        {status === 'All Statuses'
                          ? t('goals.filters.status.all')
                          : t(`goals.status.${status}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="text-muted-foreground mb-2 text-sm">
              {t('common.showing')} {filteredGoals.length} {t('goals.title')}
            </div>

            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
              {filteredGoals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onViewDetails={openGoalDetails}
                  onEdit={(goal) => openEditDialog(goal)}
                  onDelete={(goal) => {
                    setSelectedGoal(goal);
                    setIsDeleteDialogOpen(true);
                  }}
                />
              ))}
            </div>

            {filteredGoals.length === 0 && (
              <div className="py-12 text-center">
                <Target className="text-muted-foreground mx-auto mb-4 h-12 w-12 opacity-50" />
                <h3 className="mb-1 text-lg font-medium">{t('goals.noGoals')}</h3>
                <p className="text-muted-foreground">{t('goals.noGoalsDescription')}</p>
                <Button className="mt-4" onClick={openAddDialog}>
                  <Plus className="mr-2 h-4 w-4" /> {t('goals.actions.add')}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Unified Goal Dialog for Add/Edit */}
      <GoalDialog
        open={isGoalDialogOpen}
        onOpenChange={setIsGoalDialogOpen}
        onSubmit={dialogMode === 'add' ? handleAddGoal : handleEditGoal}
        onCancel={() => {
          setIsGoalDialogOpen(false);
          setSelectedGoal(null);
        }}
        initialData={selectedGoal}
        mode={dialogMode}
      />

      {/* Goal Details Dialog */}
      <Dialog
        open={isDetailsDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeGoalDetails();
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('goals.details.title')}</DialogTitle>
            <DialogDescription>{t('goals.details.description')}</DialogDescription>
          </DialogHeader>
          {selectedGoal && (
            <GoalDetails
              goal={selectedGoal}
              onEdit={() => {
                setIsDetailsDialogOpen(false);
                openEditDialog(selectedGoal);
              }}
              onDelete={() => {
                setIsDetailsDialogOpen(false);
                setIsDeleteDialogOpen(true);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Goal Confirmation Dialog */}
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteGoal}
        title={t('goals.delete.title')}
        description={t('goals.delete.description')}
      />
    </div>
  );
};

export default Goals;
